/////////////////////////////////////////////////////////////////////////
// DemoDataFeed provides simulated data for demo, as well as for testing 
// with no LiveDataFeed.
// In an application, data will be coming from LiveDataFeed.
//////////////////////////////////////////////////////////////////////////

/* eslint eqeqeq: 0, no-unused-vars: 0 */
/* eslint @typescript-eslint/no-unused-vars: 0 */

import { GLG, IsUndefined, GetCurrTime, AppError, AppLog } from './Utils.js'
import { PlotDataPoint } from './PlotData.js'
import { AlarmRecord } from './GlgAlarms.js'

/* Set to true to use demo data file in JSON format.
   Set to false to generate demo data in memory.
*/
let USE_DEMO_DATA_FILE = false;

// Demo data file to be used for testing JSON format for process_overview.g.
let DEMO_DATA_FILE_JSON = "DemoDataFile.json"; 

//////////////////////////////////////////////////////////////////////////
export function DemoDataFeed( glg_viewer )
{
   this.Viewer = glg_viewer;
   
   // Initialize datafeed as needed.
   this.Initialize();

   // Used to generate simulated demo data.
   this.counter = 0;

   this.FIRST_ALARM_RAISE_THRESHOLD = 0.6;
   this.ALARM_RAISE_THRESHOLD = 0.8;
    
   this.OLD_ACK_ALARM_AGE = 15;
   this.OLD_NON_ACK_ALARM_AGE = 30;
   this.NUM_SIMULATED_ALARMS = 30;
    
   // Keeps a list of active alarms for simulation.
   this.ActiveAlarmList = [];   /* AlarmRecord[] */
}

//////////////////////////////////////////////////////////////////////////
DemoDataFeed.prototype.Initialize = function()
{
   // Do nothing for the simulated data. For the live data,
   // provide a custom implementation of this method in LiveDataFeed.
}

//////////////////////////////////////////////////////////////////////////
DemoDataFeed.prototype.Cleanup = function()
{
   // Do nothing for the simulated data. For the live data,
   // provide a custom implementation of this method in LiveDataFeed.
}

//////////////////////////////////////////////////////////////////////////
// Query new data values. 
// tag_list:
//    An array of strings representing tag sources in the drawing, 
//    can be passed to the server to indicate which tags to obtain 
//    new data values for.
// data_callback:
//    The callback function to be invoked when the data query is finished.
//    The callback should be invoked with the new_data array containing
//    an array of objects with the following properties:
//    new_data[i].tag_source
//    new_data[i].value
// user_data:
//    User data to be passed to the data_callback.
//////////////////////////////////////////////////////////////////////////
DemoDataFeed.prototype.ReadData = function( tag_list, data_callback, user_data )
{
   if( USE_DEMO_DATA_FILE )
   {
      /* Get data from a URL (file is used for demo).
         The file is expected to be found in the directory of all .g files.
      */
      let filepath = this.Viewer.GetFullName( DEMO_DATA_FILE_JSON );
      let data_file_url = new URL( filepath, window.location.href );
      
      GLG.LoadAsset( data_file_url.toString(), 
                     GLG.GlgHTTPRequestResponseType.JSON, 
                     data_callback, user_data );
   }
   else
   {
      /* Create a JSON object from tag_list to be sent to the server, using
         only the following properties: "tag_source", "data_type".
         For demo purposes, the new data values are generated in memory 
         in JSON format. 
      */
      let tag_list_JSON =
        JSON.stringify( tag_list, [ "tag_source", "data_type" ] );
        
      //  Generate random data values in memory.
      this.GetDemoData( tag_list_JSON, data_callback, user_data  );
   }
}
////////////////////////////////////////////////////////////////////////////
// Process raw data based on their custom format.
// Create and return new_data[] array as a list of GlgDataRecord objects.
//
// If simulated data are used, this function is used only if
// USE_DEMO_DATA_FILE_JSON=true, to show an example how to process custom
// data format. An application can provide a custom implementation
// of this function in LiveDataFeed.
////////////////////////////////////////////////////////////////////////////
DemoDataFeed.prototype.ProcessRawData =
  function ( raw_data )             /* GlgDataRecord[] */
{
   /* If demo data file is not used, no processing is needed -- 
      Data are already generated by GetDemoData as expected by 
      GlgViewer.PushData().
   */
   if( !USE_DEMO_DATA_FILE || raw_data == null )
     return raw_data;
 
   let tag_list = Object.keys( raw_data );
   let num_items = tag_list.length;
   if( num_items == 0 )
      return null;

   let new_data = [];
   let
     tag_source,
     value,
     time_stamp;
  
   for( let i=0; i<num_items; ++i )
   {
      tag_source = tag_list[i];

      let elem = raw_data[tag_source];
      value = elem.value;
      time_stamp =
        ( elem.time_stamp != null ? elem.time_stamp : GetCurrTime() );

      new_data.push( { tag_source: tag_source, value: value,
                       time_stamp: time_stamp } );
   }

   return new_data;
}

//////////////////////////////////////////////////////////////////////////
// Write numerical value into the provided database tag. 
//////////////////////////////////////////////////////////////////////////
DemoDataFeed.prototype.WriteDValue =
  function ( /*String*/ tag_source, /*double*/ value )
{
   if( IsUndefined( tag_source ) )
     return;
   
   // DEMO only: Set value for a specified tag in the currently loaded drawing.
   this.Viewer.MainViewport.SetDTag( tag_source, value );
}

//////////////////////////////////////////////////////////////////////////
// Write string value into the provided database tag. 
//////////////////////////////////////////////////////////////////////////
DemoDataFeed.prototype.WriteSValue = 
  function ( /*String*/ tag_source, /*String*/ value )
{
   if( IsUndefined( tag_source ) )
     return;
   
   // DEMO only: Set value for a specified tag in the currently loaded drawing.
   this.Viewer.MainViewport.SetSTag( tag_source, value );
}

//////////////////////////////////////////////////////////////////////////
DemoDataFeed.prototype.ACKAlarm = /*boolean*/
  function( /*String*/ tag_source )
{
   /* Simulate alarm ACK in the demo mode. In a real application, 
      LiveDataFeed will send alarm ACK to the process data server.
   */
    
   let num_alarms = this.ActiveAlarmList.length;   /* int */ 
    
   // Find the alarm in the active alarm list and reset it's ACK flag.
   for( let i=0; i<num_alarms; ++i )
   {
      let alarm = this.ActiveAlarmList[i];   /* AlarmRecord */
      if( alarm.tag_source == tag_source )
      {
         alarm.ack = true;
         AppLog( "Acknowledging alarm: " + tag_source );
         return true;
      }
   }
   return false;
} 

//////////////////////////////////////////////////////////////////////////
DemoDataFeed.prototype.GetAlarms = function( alarm_callback, user_data )
{
   if( !this.Viewer.Active )
     return;
    
   /* Simulate alarms. */
    
   let alarm;   /* AlarmRecord */
    
   /* Ages alarms and removes old alarms from the list for continuous
      simulation.
   */
   this.AgeAlarms( this.ActiveAlarmList );
   let num_active_alarms = this.ActiveAlarmList.length;   /* int */
    
   let alarm_raise_threshold =    /* double */
   ( num_active_alarms == 0 ?
     this.FIRST_ALARM_RAISE_THRESHOLD : this.ALARM_RAISE_THRESHOLD );

   // Add new simulated alarm (conditionally).
   if( num_active_alarms < this.NUM_SIMULATED_ALARMS &&          
       GLG.Rand( 0.0, 1.0 ) > alarm_raise_threshold )
   {
      alarm = this.GetAlarmData(); 
      this.ActiveAlarmList.push( alarm );
      num_active_alarms = this.ActiveAlarmList.length;
   }

   let alarm_list = null;
   if( num_active_alarms > 0 )
   {
      // Create a new list of alarms to be returned.
      alarm_list = [];   /* AlarmRecord[] */
        
      /* For simulating alarms in the demo, populate the list with alarms from 
         ActiveAlarmList.
      */
      for( let i=0; i<num_active_alarms; ++i )
      {
         alarm = this.ActiveAlarmList[i];
         alarm_list.push( alarm );
      }
   }

   // Invoke the callback with new alrm list.
   alarm_callback( alarm_list );
} 

//////////////////////////////////////////////////////////////////////////
// If the incoming value is used in a chart's plot, check if the value
// is valid for this datasample. The value must be a double. 
// For demo purposes, the function always returns true. 
// The application should provide a custom  implementation of this method 
// in LiveDataFeed. If the function returns false, the plot's ValidEntryPoint 
// will be 0 and the plot will have a hole for this datasample.
//////////////////////////////////////////////////////////////////////////
DemoDataFeed.prototype.IsValid = /* bool */ 
  function( /*String*/ tag_source, /*int*/ data_type, value )
{
   let result = false;
   switch( data_type )
   {
    case GLG.GlgDataType.D: 
      result = true;
      break;
    default:
      break;
   } 
   
   return result;
}

//////////////////////////////////////////////////////////////////////////
// Generate simulated demo data for all tags listed in tag_list_JSON.
// Simulates the http response the application will create
// using custom http request for data acquisition.
//////////////////////////////////////////////////////////////////////////
DemoDataFeed.prototype.GetDemoData =
  function( tag_list_JSON, data_callback, user_data )
{
   let tag_list = JSON.parse( tag_list_JSON );
   let new_data = [];
   let value, time_stamp;

   for( let i=0; i<tag_list.length; ++i )
   {
      // DEMO only: don't push new data to tags with TagSource="State". 
      if( tag_list[i].tag_source == "State" )
        continue;

      switch( tag_list[i].data_type )
      {
       case GLG.GlgDataType.D:
         // Obtain new numerical data value for a specified tag_source.
          value = this.GetDemoValue( tag_list[i].tag_source, false );
         break;
       case GLG.GlgDataType.S:
         // Obtain new string value for a specified tag_source.
         value = this.GetDemoString( tag_list[i].tag_source );
         break;

       default:
         AppError( "Unsupported data type." );
         break;
      }

      time_stamp = GetCurrTime();

      // Add new element to the new_data array.
      new_data.push( { tag_source: tag_list[i].tag_source, 
                       value: value,
                       time_stamp: time_stamp } );
   }

   // Invoke the callback with new_data.
   data_callback( new_data, user_data );
}

//////////////////////////////////////////////////////////////////////////
// Generate a simulated numerical data value. 
//////////////////////////////////////////////////////////////////////////
DemoDataFeed.prototype.GetDemoValue = /* double */ 
  function( /*String*/ tag_source, /*bool*/ historical_mode )
{
   let  /* double */
   center,
   amplitude,
   period,
   low,
   high;
    
   switch( this.Viewer.PageType )
   {
    case "RealTimeChart":
      if( tag_source.startsWith( "Volts" ) )
      {
         low = 0.;
         high = 500.;
         period = 300.0;
         amplitude = 40.0;
         center = 380.0;
      }
      else if( tag_source.startsWith( "Amp" ) )
      {
         low = 0.;
         high = 50.;
         period = 1000.0;
         amplitude = 15.0;
         center = 30.0;
      }
      else 
      {
         low = 0.;
         high = 50.;
         period = 1000.0;
         amplitude = 30.0;
         center = 50.0;
      }
        
      if( historical_mode )
        // Historical data, data were saved once per second.
        this.counter += 10;  
      else
        // Real-time mode.
        ++this.counter; 
      break;

    case "TestCommands":
      if( tag_source.toLowerCase().includes( "fuel" ) )
      {
         low = 0.;
         high = 500.;
         period = 1000.0;
         amplitude = 80.;
         center = 350.;
      }
      else if( tag_source.toLowerCase().includes( "temp" ) )
      {
         low = 0.;
         high = 90.;
         period = 1000.0;
         amplitude = 25.;
         center = 50.;
      }
      else  //default
      {
         low = 0.;
         high = 100.;
         period = 500.0;
         amplitude = ( high - low ) / 2.0;
         center = low + amplitude;
      }

      if( tag_source.toLowerCase().includes( "low" ) )
        return low;
      else if( tag_source.toLowerCase().includes( "high" ) )
        return high;

      if( historical_mode )
        // Historical data, data were saved once per second.
        this.counter += 10;
      else
        ++this.counter; 
      break;

    case "Default":
    default:
      low = 0.;
      high = 100;
      period = 500.0;
      amplitude = ( high - low ) / 2.0;
      center = low + amplitude;

      ++this.counter; 
      break;
   }

   let alpha = 2.0 * Math.PI * this.counter / period;
   let value = center + 
   amplitude * Math.sin( alpha ) * Math.sin( alpha / 30.0 );
    
   return value;
}

/////////////////////////////////////////////////////////////////////// 
DemoDataFeed.prototype.GetDemoString = /* String */
   function( /*String*/ tag_source )
{
   return "DEMO";
}

/////////////////////////////////////////////////////////////////////// 
// Ages alarms and removes old alarms from the list for continuous
// simulation.
///////////////////////////////////////////////////////////////////////
DemoDataFeed.prototype.AgeAlarms = function( /* AlarmRecord[] */ alarm_list )
{
   let num_alarms = alarm_list.length;
   for( let i=0; i<num_alarms; ++i )
   {
      let alarm = alarm_list[i];    /* AlarmRecord */
      ++alarm.age;
        
      if( ( alarm.ack && alarm.age > this.OLD_ACK_ALARM_AGE ) ||
          ( !alarm.ack && alarm.age > this.OLD_NON_ACK_ALARM_AGE ) )
      {
         this.RemoveArrayElement( alarm_list, alarm );
         return;
      }
   }
}

////////////////////////////////////////////////////////////////////////
DemoDataFeed.prototype.RemoveArrayElement = function( array, data )
{
   for( let i=0; i<array.length; ++i )
     if( array[i] == data )
     {
        array.splice( i, 1 );
        return;
     }
}

//////////////////////////////////////////////////////////////////////////
DemoDataFeed.prototype.GetAlarmData = function()   /* AlarmRecord */
{
   /* Simulate alarms for the demo. In a real application, LiveDataSource
      will query the list of alarms from the process data server.
   */
    
   let alarm_status = Math.trunc( GLG.Rand( 1.0, 3.99 ) );   /* int */
    
   let ch = ( 'A' + Math.trunc( GLG.Rand( 0.0, 26.9 ) ) );          /* String */
   let alarm_source = ch + Math.trunc( GLG.Rand( 100.0, 999.0 ) );  /* String */
    
   let description;          /* String */
   let string_value = null;  /* String  */
   let double_value = 0.0;   /* double */
   let random_message;       /* int */
    
   if( alarm_status < 3 )
   {
      random_message = ( GLG.Rand( 0.0, 10.0 ) < 5.0 ? 0 : 1 );
        
      switch( random_message )
      {
       default:
       case 0:
         description = "Tank #% low";
         double_value = GLG.Rand( 100.0, 150.0 ); 
         break;
            
       case 1: 
         description = "Tank #% high";
         double_value = GLG.Rand( 600.0, 900.0 ); 
         break;
      }
   }
   else
   {
      random_message = Math.trunc( GLG.Rand( 0.0, 2.99 ) );
      switch( random_message )
      {
       default:
       case 0: description = "Breaker #%"; string_value = "TRIPPED"; break;
       case 1: description = "Fuse #%"; string_value = "BLOWN"; break;
       case 2: description = "Tank #%"; string_value = "OVERFLOW"; break;
      }
   }
    
   let random_number = Math.trunc( GLG.Rand( 1.0, 100.0 ) );   /* int */
   description = GLG.CreateIndexedName( description, random_number );
    
   let alarm_time = GetCurrTime();   /* double */
    
   let alarm = new AlarmRecord();
   alarm.time = alarm_time;
   alarm.tag_source = alarm_source;
   alarm.description = description;
   alarm.string_value = string_value;
   alarm.double_value = double_value;
   alarm.status = alarm_status;
   alarm.ack = false;
   return alarm;
}

/////////////////////////////////////////////////////////////////////// 
// Get historical data for the plot with a specified tag.
/////////////////////////////////////////////////////////////////////// 
DemoDataFeed.prototype.GetPlotData = /* PlotDataPoint[] */
  function ( /*String*/ tag_source, /*double*/ start_time, 
             /*double*/ end_time, /*int*/ max_num_samples,
             /*callback*/ data_callback, /*user data*/ user_data)
{
   /* In a real application, the number of data points to be queried
      is determined by the start and end time. For the demo, return
      the requested max number of points.
   */
   if( max_num_samples < 1 )
     max_num_samples = 1;
   let num_samples = max_num_samples;   /* int */
    
   let interval = ( end_time - start_time ) / max_num_samples;   /* double */

   let data_array = [];   /* PlotDataPoint[] */
   for( let i=0; i<num_samples; ++i )
   {
      /* Generate demo data. */
      let value = this.GetDemoValue( tag_source, /*historical*/ true );
      let time_stamp = start_time + interval * i;
      let value_valid = true;
        
      let data_point = new PlotDataPoint( value, time_stamp, value_valid );
      data_array.push( data_point );
   }
    
   // Invoke data callback.
   data_callback( data_array, user_data );
}
